import { FC, useState } from 'react';
import { CircleAlert } from 'lucide-react';
import { toast } from 'react-toastify';
import { funnelsAPI } from 'services/swagger-api';
import { useDialog } from 'hooks/useDialog';
import { parseApiError } from 'shared/utils/apiValidationErrors';
import { ModalShell } from 'back-office/shared/components/ModalShell';
import { TunnelActionTarget } from '../tunnelActionTarget';

interface DeleteTunnelModalProps {
  tunnel: TunnelActionTarget;
  /** Après suppression : rafraîchir la liste, ou quitter la fiche du tunnel supprimé. */
  onDeleted?: () => void;
}

/**
 * Suppression (logique) d'un tunnel.
 *
 * L'action est proposée sur tous les tunnels : c'est le back qui tranche, en énumérant ce
 * qui référence le tunnel (devis, contrats, contrats collectifs, paniers emprunteur,
 * mandats, leads partenaires) et en orientant vers la désactivation. Son refus arrive en
 * HTTP 200 avec isSuccess à false : le message est affiché tel quel, sans reformulation.
 */
export const DeleteTunnelModal: FC<DeleteTunnelModalProps> = ({ tunnel, onDeleted }) => {
  const { closeDialog } = useDialog();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (isDeleting) return;

    setIsDeleting(true);
    setError(null);
    try {
      const response = await funnelsAPI.deleteFunnel({ funnelId: tunnel.id });

      if (!response.isSuccess) {
        setError(response.errorMessage || 'La suppression du tunnel a été refusée.');
        return;
      }

      toast.success('Tunnel supprimé');
      onDeleted?.();
      closeDialog();
    } catch (thrown) {
      console.error('Suppression du tunnel impossible', thrown);
      const parsed = await parseApiError(thrown);
      setError(parsed.message || 'La suppression du tunnel a échoué.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <ModalShell
      title="Supprimer le tunnel"
      subtitle={tunnel.label || tunnel.code || undefined}
      submit={{
        label: isDeleting ? 'Suppression…' : 'Supprimer',
        onClick: handleDelete,
        disabled: isDeleting,
        destructive: true,
      }}
      closeLabel="Annuler"
    >
      <div className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-5">
        <p className="text-sm text-gray-700">
          Le tunnel{' '}
          <span className="font-semibold">{tunnel.label || tunnel.code || 'sélectionné'}</span>{' '}
          sera supprimé, avec ses garanties, franchises, plafonds, exclusions, commissions et
          frais.
        </p>

        <p className="text-sm text-gray-500">
          La suppression est refusée si un devis, un contrat, un contrat collectif, un panier
          emprunteur, un mandat ou un lead partenaire pointe encore le tunnel. Dans ce cas,
          désactivez-le : il disparaît de la souscription sans casser l'existant.
        </p>

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            <CircleAlert className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>
    </ModalShell>
  );
};

export default DeleteTunnelModal;
